import {
  SoopButtonCommon,
  SoopButtonIcon,
  SoopImage,
  SoopInnerLnb,
  SoopSidebar,
  SoopSideContent,
  SoopNavContent,
  SoopAvatarCommon,
} from '@soop-ui/soop-ui';
import styles from './ServiceLeftMenu.module.scss';
import TopfanLayer from '../../Layer/Topfan';
import TopsubscriptionLayer from '../../Layer/Subscription';
import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import useGlobalUIStore from '@/stores';
import {RouterLink} from '@/components/channel/common/RouterLink';
import {ROUTE} from '@/constants/main/route';
import Skeleton from './Skeleton';

import {useStationMenu} from '@/hooks/channel/Sidebar/useStationSideMenu';
import {useDeviceDetection} from '@/hooks/channel/useDeviceDetection';
import {useStationData, useStationUI} from '@/stores/channel/station';
import useChannelClickLog from '@/hooks/channel/useChannelClickLog';
import ProfileInfo from '@/components/channel/pc/layout/LeftMenu/ProfileInfo';
import {getUserLoginId} from '@/utils/main';
import ChannelInfoLayer from '@/components/channel/pc/home/ChannelVisual/ChannelInfo/InfoLayer';

const MemoizedSoopNavContent = React.memo(SoopNavContent);

const ChannelServiceLeftMenu = () => {
  const navigate = useNavigate();
  const {isDarkMode, isLnbOpened} = useGlobalUIStore();
  const deviceType = useDeviceDetection('userAgent');
  const {sendClickLog} = useChannelClickLog();
  /**
   * 구독자 목록 표시 상태 (구독팬 순위 / 구독 선물 순위)
   */
  const [showSubsFanList, setShowSubsFanList] = useState(true);
  const {error, loading} = useStationUI();

  const {
    userId,
    isOwner,
    isManager,
    profileMenuItems,
    sidebarMenuItems,
    settingMenuItems,
    sidebarTopFanList,
    sidebarSubscribeGiftList,
    sidebarSubscriberList,
    isAllLoaded,
    dataUpdatedAt,
  } = useStationMenu(isLnbOpened);

  const [isFanLayerOpen, setIsFanLayerOpen] = useState(false);
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const [isInfoLayerOpen, setIsInfoLayerOpen] = useState(false);
  const [isTopFanActive, setIsTopFanActive] = useState(false);
  const [isSubsFanActive, setIsSubsFanActive] = useState(false);

  const fanRef = useRef<HTMLDivElement>(null);
  const subscriptionRef = useRef<HTMLDivElement>(null);

  const handleOpenInfoLayer = async () => {
    setIsInfoLayerOpen(true);
    setIsSubscriptionOpen(false);
    setIsFanLayerOpen(false);
    if (!isInfoLayerOpen) {
      await sendClickLog('station_click', {
        button_group: 'station_info',
        button_type: 'info_icon',
      });
    }
  };

  const fanLayer = async () => {
    setIsTopFanActive(true);
    setIsSubsFanActive(false);
    setIsFanLayerOpen(true);
    setIsSubscriptionOpen(false);
    setIsInfoLayerOpen(false);
    if (!isFanLayerOpen) {
      await sendClickLog('station_click', {
        button_group: 'station_lnb',
        button_type: 'more_topfan',
      });
    }
  };

  const subscriptionLayer = async () => {
    // Note. 데이터 없을 경우엔 레이어 미노출
    if (
      (showSubsFanList && sidebarSubscriberList.length > 0) ||
      (!showSubsFanList && sidebarSubscribeGiftList.length > 0)
    ) {
      setIsTopFanActive(false);
      setIsSubsFanActive(true);
      setIsSubscriptionOpen(true);
      setIsFanLayerOpen(false);
      setIsInfoLayerOpen(false);

      if (!isSubscriptionOpen) {
        await sendClickLog('station_click', {
          button_group: 'station_lnb',
          button_type: showSubsFanList
            ? 'more_subscribe'
            : 'more_subscriptionpresent ',
        });
      }
    }
  };

  const subscriptionChangeList = async () => {
    setIsFanLayerOpen(false);
    setIsSubsFanActive(false);
    setIsSubscriptionOpen(false);
    setIsTopFanActive(false);
    setShowSubsFanList((prev) => !prev);

    await sendClickLog('station_click', {
      button_group: 'station_lnb',
      button_type: showSubsFanList
        ? 'change_subscriptionpresent'
        : 'change_subscribe ',
    });
  };

  // 작은 해상도에서 노출되는 햄버거 메뉴 클릭 시 : 이건 공통GNB와 별개로 구현된 부분이라 어쩔 수 없다.
  const handleLnbClose = () => {
    document
      .querySelector<HTMLButtonElement>('#topInnerHeader .btn_flexible button')
      ?.click();
  };

  useEffect(() => {
    // 구독 팬 랭킹 목록이 없으면서 구독 선물 랭킹 목록이 있을 경우에만 구독 선물 우선 노출
    setShowSubsFanList(
      sidebarSubscriberList.length > 0 || sidebarSubscribeGiftList.length === 0,
    );
  }, [sidebarSubscriberList, sidebarSubscribeGiftList]);

  useEffect(() => {
    setIsTopFanActive(false);
    setIsSubsFanActive(false);
    setIsInfoLayerOpen(false);
    setIsSubscriptionOpen(false);
    setIsFanLayerOpen(false);
  }, [isLnbOpened]);

  const {station} = useStationData();
  // 빈문자열도 없는값으로 처리가 되어 type 으로 구분
  const hasStationInfo = typeof station?.detail?.name === 'string';

  if (error || loading) {
    return null;
  }

  if (!isAllLoaded) {
    return deviceType === 'pc' ? <Skeleton /> : null;
  }

  return deviceType === 'pc' && hasStationInfo ? (
    <SoopSidebar
      key={dataUpdatedAt}
      className={`${styles.ChannelServiceLeftMenu} ${
        isLnbOpened ? styles.expanded : styles.contracted
      }`}
      variant={isLnbOpened ? 'Expanded' : 'Contracted'}>
      <div className={styles.sidebarHeader}>
        <SoopButtonIcon
          circle
          iconName='icoSwitch'
          size={40}
          className={styles.fixBtn}
          startIconName='icoEmoticon'
          variant='clearPrimary'
          onClick={handleLnbClose}
        />
        <RouterLink
          name={ROUTE.CHANNEL.SERVICE.HOME}
          params={{streamerId: userId ?? ''}}>
          <SoopImage
            src={
              !isDarkMode
                ? '//res.sooplive.co.kr/images/svg/soop_logo.svg'
                : '//res.sooplive.co.kr/images/svg/soop_logo_dark.svg'
            }
            alt='SOOP'
          />
        </RouterLink>
      </div>
      <div className={styles.dimmed} onClick={handleLnbClose}></div>
      <SoopInnerLnb
        className={styles.innerLnb}
        variant={isLnbOpened ? 'Expanded' : 'Contracted'}>
        {isLnbOpened ? (
          <ProfileInfo setIsInfoLayerOpen={handleOpenInfoLayer} />
        ) : (
          <RouterLink
            name={ROUTE.CHANNEL.SERVICE.HOME}
            params={{streamerId: userId ?? ''}}
            className={styles.profileWrap}>
            <SoopAvatarCommon
              mode='static'
              size={24}
              type='button'
              userId={userId}
              variant='default'
              forceUpdate={userId === getUserLoginId()}
            />
            <span className={styles.nick}>{station?.detail?.nickname}</span>
          </RouterLink>
        )}
        {!isLnbOpened ? (
          <MemoizedSoopNavContent
            sidebarMenuItems={profileMenuItems}
            variant={'Contracted'}
          />
        ) : null}

        {((sidebarTopFanList?.length ?? 0) > 0 ||
          (sidebarSubscriberList?.length ?? 0) > 0 ||
          (sidebarSubscribeGiftList?.length ?? 0) > 0) && (
          <div className={styles.fanWrapper}>
            {sidebarTopFanList?.length > 0 && (
              <div ref={fanRef}>
                <SoopSideContent
                  variant={isLnbOpened ? 'Expanded' : 'Contracted'}
                  menuType='rank'
                  menuTitle='열혈팬'
                  rankType='topFan'
                  hasMenuSideButton={false}
                  isActive={isTopFanActive}
                  onMenuHeaderClick={fanLayer}
                  menuItems={sidebarTopFanList}
                  className={styles.topFanList}
                />
              </div>
            )}

            {(sidebarSubscriberList?.length ?? 0) > 0 ||
            (sidebarSubscribeGiftList?.length ?? 0) > 0 ? (
              <div ref={subscriptionRef}>
                <SoopSideContent
                  variant={isLnbOpened ? 'Expanded' : 'Contracted'}
                  menuType='rank'
                  menuTitle={showSubsFanList ? '구독팬' : '구독 선물'}
                  className={styles.subsFanList}
                  isActive={isSubsFanActive}
                  rankType='subscribe'
                  hasMenuSideButton={false}
                  onMenuHeaderClick={subscriptionLayer}
                  menuItems={
                    showSubsFanList
                      ? (sidebarSubscriberList ?? [])
                      : (sidebarSubscribeGiftList ?? [])
                  }
                />

                {(sidebarSubscriberList?.length ?? 0) > 0 &&
                  (sidebarSubscribeGiftList?.length ?? 0) > 0 && (
                    <div className={styles.changeButtonContainer}>
                      <SoopButtonCommon
                        className={styles.changeButton}
                        onClick={subscriptionChangeList}
                        size={32}
                        startIconName='icoChange'
                        variant='lineSecondary'>
                        {showSubsFanList ? '구독 선물 랭킹' : '구독팬 랭킹'}
                      </SoopButtonCommon>
                    </div>
                  )}
              </div>
            ) : null}
          </div>
        )}

        <div>
          <MemoizedSoopNavContent
            sidebarMenuItems={sidebarMenuItems}
            variant={isLnbOpened ? 'Expanded' : 'Contracted'}
          />
        </div>

        {(isOwner || isManager) && (
          <div className={styles.sideWriteButton}>
            <SoopButtonIcon
              onClick={() => navigate(`/station/${userId}/setting`)}
              size={40}
              startIconName='icoSetting'
              variant='defaultSecondary'>
              방송국 관리
            </SoopButtonIcon>
          </div>
        )}
        {!isLnbOpened && (isOwner || isManager) ? (
          <MemoizedSoopNavContent
            sidebarMenuItems={settingMenuItems}
            variant={'Contracted'}
          />
        ) : null}
      </SoopInnerLnb>
      {isInfoLayerOpen && <ChannelInfoLayer layerOpen={{setIsInfoLayerOpen}} />}
      {isFanLayerOpen && (
        <TopfanLayer
          isCollapse={!isLnbOpened}
          streamerId={userId!}
          onClose={() => {
            setIsFanLayerOpen(false);
            setIsTopFanActive(false);
          }}
          style={{
            top: Math.min(
              (fanRef.current?.getBoundingClientRect()?.top || 0) - 65,
              window.innerHeight - 520,
            ),
          }}
        />
      )}

      {isSubscriptionOpen && (
        <TopsubscriptionLayer
          streamerId={userId!}
          isCollapse={!isLnbOpened}
          onClose={() => {
            setIsSubscriptionOpen(false);
            setIsSubsFanActive(false);
          }}
          type={showSubsFanList ? 'acc' : 'gift'}
          style={{
            top: Math.min(
              (subscriptionRef.current?.getBoundingClientRect()?.top || 0) - 65,
              window.innerHeight - 520,
            ),
          }}
        />
      )}
    </SoopSidebar>
  ) : null;
};

export default ChannelServiceLeftMenu;
